import Badge from '@/app/common/components/Badge.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import Image from '@/app/common/components/Image.tsx'
import PageLoader from '@/app/common/components/PageLoader.tsx'
import { InfoCircleIcon } from '@/app/common/icons/general.tsx'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import LoadingCard from '@/common/components/LoadingCard.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { currencyFormatter } from '@/lib/utils.ts'
import { ProviderQueries } from '@/queries/providers/queries.ts'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/shad/ui/command.tsx'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { selectSelectedProgram } from '@/storage/programs/slice.ts'
import { RootState } from '@/storage/store.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { RichTextViewer } from '../AddProduct/RichTextEditor'
import UnpublishedChangesBanner from '../configureStore/components/unPublishedChangesBanner.tsx'
import EditProduct from './EditProduct.tsx'
import {
  AcceptRejectProviderItemParams,
  DecisionFormData,
  FormSchema,
} from './schema.ts'

export default function MarketplaceTabPage() {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const { toast } = useToast()

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const providerId = useParams().providerId!
  const programId = useSelector(selectSelectedProgram).id

  const mutation = useMutation({
    mutationFn: (params: AcceptRejectProviderItemParams) =>
      ProviderQueries.acceptRejectProviderItem(
        params.providerId,
        params.itemId,
        params.programId,
        params.status,
        params.justification
      ).mutationFn(),
  })

  const {
    data: categories,
    refetch,
    status,
  } = useQuery(
    ProviderQueries.getProviderItemCategories(
      Number(providerId),
      selectedProgramYear?.id ?? 0
    )
  )

  const {
    register,
    formState: { errors },
    setError,
    watch,
  } = useForm<DecisionFormData>({
    resolver: zodResolver(FormSchema),
  })

  const handleAction = (status: 1 | 2) => {
    const justificationRaw = watch('justification')
    const justification = justificationRaw || undefined

    if (status === 2 && !justification) {
      setError('justification', {
        type: 'manual',
        message: 'Justification is required for rejection.',
      })
      return
    }

    mutation.mutate(
      {
        providerId: Number(providerId),
        itemId: currentProviderItem?.id ?? 0,
        programId: selectedProgramYear?.id ?? 0,
        status,
        justification,
      },
      {
        onSuccess: () => {
          toast({
            variant: 'default',
            title: 'updated successfully.',
          })
          refetch()
        },
        onError: (error) => {
          toast({
            title: 'Error',
            variant: 'destructive',
            description: error.message,
          })
        },
      }
    )
  }

  const providerPreviewInformation = useQuery(
    ProviderQueries.getProviderPreviewSettings(
      selectedProgramYear?.id ?? 0,
      providerId ?? ''
    )
  )

  const providerItems =
    categories
      ?.find((c) => c.id === selectedCategory)
      ?.provider_items.filter(
        (item) => item.provider_id === Number(providerId)
      ) || []

  const currentProviderItem =
    providerItems.find((item) => item.id === selectedProduct) || null

  const handleCategorySelect = (id: number) => {
    setSelectedCategory(id)
    setSelectedProduct(null)
  }

  const handleProductSelect = (id: number) => {
    setSelectedProduct(id)
  }

  if (status === 'pending') return <LoadingCard title="Loading..." />
  if (status === 'error') {
    return <div>error</div>
  }

  return (
    <PageLoader query={providerPreviewInformation}>
      {(previewInformation) => (
        <div>
          <div className="mb-4">
            <UnpublishedChangesBanner
              providerPreviewInformation={previewInformation}
              providerId={providerId}
              programId={programId}
            />
          </div>
          <div className="grid grid-cols-12 gap-x-4 items-start">
            {/* Product Selection Card */}
            <Card className="col-span-4">
              <CardHeader className="flex justify-between items-center text-md font-semibold">
                <div className="text-left text-md font-semibold text-gray-900">
                  Store - Product Selection
                </div>
              </CardHeader>
              <CardContent className="px-4 py-6 sm:p-8">
                <div className="mb-4 relative">
                  <label className="text-sm font-medium mb-1">
                    Marketplace Items
                  </label>
                  <Command>
                    <CommandInput placeholder="Search items..." />
                    <CommandList className="h-[30rem] overflow-y-auto">
                      <CommandEmpty>No items found.</CommandEmpty>
                      {categories?.map((category) => (
                        <CommandGroup key={category.id} heading={category.name}>
                          {category.provider_items.map((item) => (
                            <CommandItem
                              className="w-full min-w-0 whitespace-nowrap overflow-hidden text-ellipsis data-[selected=true]:bg-gray-100 rounded-md"
                              key={category.name + item.id}
                              value={item.name + item.id}
                              onSelect={() => {
                                setEditModalOpen(false)
                                handleCategorySelect(category.id)
                                handleProductSelect(item.id)
                              }}
                            >
                              <span
                                className="truncate block w-full"
                                title={item.name}
                              >
                                {item.name}{' '}
                                {item.status_name === 'Pending' && (
                                  <div className="mr-2 text-gray-600 text-xs">
                                    <div>
                                      <Badge
                                        semanticColor="Notice"
                                        className=""
                                      >
                                        Pending
                                        <InfoCircleIcon className="ml-1" />
                                      </Badge>
                                    </div>
                                  </div>
                                )}
                              </span>
                            </CommandItem>
                          ))}
                          <CommandSeparator />
                        </CommandGroup>
                      ))}
                    </CommandList>
                  </Command>
                </div>
              </CardContent>
            </Card>

            {/* Product Details Card */}
            {selectedProduct && currentProviderItem && !editModalOpen ? (
              <Card className="col-span-8">
                <CardHeader className="flex justify-between items-center text-md font-semibold">
                  <div className="text-left text-md font-semibold text-gray-900">
                    Product Details
                  </div>
                  <Button
                    variant={'outline'}
                    onClick={() => {
                      setEditModalOpen(true)
                    }}
                  >
                    Edit
                  </Button>
                </CardHeader>
                <CardContent className="px-4 py-6 sm:p-8">
                  <div>
                    <div className="flex items-center">
                      {currentProviderItem?.image && (
                        <Image
                          key={currentProviderItem.image}
                          allowZoom={true}
                          className="h-64 w-64 object-cover rounded-lg"
                          path={currentProviderItem.image}
                        />
                      )}
                    </div>
                    <div className="mb-6 text-left">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mt-6">
                          {currentProviderItem.name}
                        </h3>
                      </div>
                      <div className="mb-2">
                        <div className="text-sm text-gray-800">Type:</div>
                        <p className="mb-1">{currentProviderItem.type}</p>
                      </div>
                      <div className="mb-2">
                        <div className="font-medium text-sm text-gray-800">
                          Price:
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">
                            {currencyFormatter.format(
                              currentProviderItem.price
                            )}
                          </span>
                        </div>
                      </div>
                      {currentProviderItem.created_at && (
                        <div className="mb-2">
                          <div className="font-medium text-sm text-gray-800">
                            Created At:
                          </div>{' '}
                          <div className="text-sm text-gray-600">
                            {new Date(
                              currentProviderItem.created_at
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {currentProviderItem.keyword_tags && (
                        <div className="mb-2">
                          <div className="font-medium text-sm text-gray-800">
                            Tags:
                          </div>{' '}
                          <div className="text-sm text-gray-600">
                            {currentProviderItem.keyword_tags}
                          </div>
                        </div>
                      )}
                      {currentProviderItem.comments && (
                        <div className="mb-2">
                          <div className="font-medium text-sm text-gray-800">
                            Description
                          </div>
                          <div className="text-sm mt-2 text-gray-600 ">
                            <RichTextViewer
                              className="border-0 "
                              value={currentProviderItem.comments}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {currentProviderItem?.status === 1 && (
                      <div className="">
                        <label className="text-sm font-medium mb-1">
                          Description
                        </label>
                        <Textarea
                          rows={7}
                          placeholder="Comment"
                          {...register('justification')}
                        />
                        {errors.justification && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.justification.message}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-x-6 mb-6">
                      {currentProviderItem?.status === 1 && (
                        <SubmitButton
                          buttonTxt="Reject"
                          className="w-auto"
                          status={mutation.status}
                          onClick={() => handleAction(2)}
                        />
                      )}
                    </div>
                    <div className="space-x-6 mb-6">
                      {[0, 2].includes(currentProviderItem?.status) && (
                        <SubmitButton
                          buttonTxt="Accept"
                          className="w-auto"
                          status={mutation.status}
                          onClick={() => handleAction(1)}
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : editModalOpen && currentProviderItem ? (
              <Card className="w-full col-span-8">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="text-left text-md font-semibold text-gray-900">
                      Edit Product
                    </div>
                    <Button
                      variant={'outline'}
                      onClick={() => {
                        setEditModalOpen(false)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <EditProduct
                    product={currentProviderItem}
                    refetch={refetch}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card className="w-full h-full flex items-center justify-center col-span-8 ">
                <CardContent className="h-full w-full flex items-center justify-center">
                  <div className="text-center text-gray-700">
                    Please select a product to view details.
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </PageLoader>
  )
}
